import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import type { EmailCampaign } from "../../../types/marketing";
import {
  allEmailRows,
  type AudienceContact,
  type AudienceSegment,
} from "./emailWorkflows";
export function ContactSelection({
  label,
  ids,
  onChange,
}: {
  label: string;
  ids: number[];
  onChange: (ids: number[]) => void;
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const contacts = useQuery({
    queryKey: ["email-contact-options", search, page],
    queryFn: async () =>
      (
        await api.get<{ results: AudienceContact[]; count: number }>(
          `/api/marketing/email-campaigns/audience-options/?${new URLSearchParams({ search, page: String(page) })}`,
        )
      ).data,
  });
  return (
    <Stack spacing={1}>
      <Typography level="title-sm">
        {label} · {ids.length} selected
      </Typography>
      <Input
        aria-label={`Search ${label.toLowerCase()}`}
        placeholder="Search contacts by name or email"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setPage(1);
        }}
      />
      <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.5}>
        {ids.map((id) => (
          <Chip
            key={id}
            endDecorator={
              <Button
                size="sm"
                variant="plain"
                aria-label={`Remove contact ${id}`}
                onClick={() => onChange(ids.filter((value) => value !== id))}
              >
                ×
              </Button>
            }
          >
            {contacts.data?.results.find((c) => c.id === id)?.name ||
              `Contact ${id}`}
          </Chip>
        ))}
      </Stack>
      {contacts.isError && (
        <Alert
          color="danger"
          endDecorator={
            <Button onClick={() => contacts.refetch()}>Retry</Button>
          }
        >
          Contacts unavailable.
        </Alert>
      )}
      <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
        {contacts.data?.results.map((contact) => (
          <Box key={contact.id} sx={{ p: 1 }}>
            <Checkbox
              checked={ids.includes(contact.id)}
              onChange={() =>
                onChange(
                  ids.includes(contact.id)
                    ? ids.filter((id) => id !== contact.id)
                    : [...ids, contact.id],
                )
              }
              label={`${contact.name} · ${contact.email || "No email"}${contact.do_not_email ? " · Opted out" : ""}`}
            />
          </Box>
        ))}
      </Box>
      <Stack direction="row" spacing={1} justifyContent="space-between">
        <Button
          size="sm"
          variant="plain"
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
        >
          Previous contacts
        </Button>
        <Typography level="body-xs">
          {contacts.data?.count || 0} matches
        </Typography>
        <Button
          size="sm"
          variant="plain"
          disabled={page * 25 >= (contacts.data?.count || 0)}
          onClick={() => setPage(page + 1)}
        >
          Next contacts
        </Button>
      </Stack>
    </Stack>
  );
}
export function AudienceSelector({
  draft,
  onChange,
  allowSegments = true,
}: {
  draft: Partial<EmailCampaign>;
  onChange: (patch: Partial<EmailCampaign>) => void;
  allowSegments?: boolean;
}) {
  const options = useQuery({
    queryKey: ["email-audience-options"],
    queryFn: async () =>
      (
        await api.get<{ tags: { id: number; name: string }[] }>(
          "/api/marketing/email-campaigns/audience-options/",
        )
      ).data,
  });
  const segments = useQuery({
    queryKey: ["email-audiences"],
    queryFn: () =>
      allEmailRows<AudienceSegment>("/api/marketing/email-audiences/"),
    enabled: allowSegments,
  });
  return (
    <Stack spacing={2}>
      <FormControl>
        <FormLabel>Audience</FormLabel>
        <Select
          value={draft.audience_type || "all"}
          onChange={(_, value) => onChange({ audience_type: value || "all" })}
        >
          <Option value="all">All organization contacts</Option>
          <Option value="tags">By tags</Option>
          <Option value="contact_type">By contact type</Option>
          <Option value="manual">Manual selection</Option>
          {allowSegments && <Option value="segment">Saved audience</Option>}
        </Select>
      </FormControl>
      {draft.audience_type === "tags" && (
        <FormControl>
          <FormLabel>Include contacts matching any tag</FormLabel>
          <Select
            multiple
            value={draft.audience_tags || []}
            onChange={(_, value) => onChange({ audience_tags: value })}
          >
            {options.data?.tags.map((tag) => (
              <Option key={tag.id} value={tag.id}>
                {tag.name}
              </Option>
            ))}
          </Select>
          {options.isError && (
            <Alert
              color="danger"
              endDecorator={
                <Button onClick={() => options.refetch()}>Retry</Button>
              }
            >
              Tags unavailable.
            </Alert>
          )}
        </FormControl>
      )}
      {draft.audience_type === "contact_type" && (
        <FormControl>
          <FormLabel>Contact type</FormLabel>
          <Select
            value={draft.audience_contact_type || ""}
            onChange={(_, value) =>
              onChange({ audience_contact_type: value || "" })
            }
          >
            {["lead", "prospect", "customer", "past_customer", "vendor"].map(
              (value) => (
                <Option key={value} value={value}>
                  {value.replace("_", " ")}
                </Option>
              ),
            )}
          </Select>
        </FormControl>
      )}
      {draft.audience_type === "segment" && (
        <FormControl>
          <FormLabel>Saved audience</FormLabel>
          <Select
            value={draft.audience_segment || null}
            onChange={(_, value) => onChange({ audience_segment: value })}
          >
            {segments.data?.map((segment) => (
              <Option key={segment.id} value={segment.id}>
                {segment.name}
              </Option>
            ))}
          </Select>
          {segments.isError && (
            <Alert color="danger">Saved audiences unavailable.</Alert>
          )}
        </FormControl>
      )}
      {draft.audience_type === "manual" && (
        <ContactSelection
          label="Included contacts"
          ids={draft.manual_contacts || []}
          onChange={(ids) => onChange({ manual_contacts: ids })}
        />
      )}
      <ContactSelection
        label="Excluded contacts"
        ids={draft.excluded_contacts || []}
        onChange={(ids) => onChange({ excluded_contacts: ids })}
      />
      <Alert color="neutral">
        Opt-outs, invalid or missing email addresses, duplicates, and explicit
        exclusions are removed. Delivery rechecks eligibility before each
        message.
      </Alert>
    </Stack>
  );
}
